/**
 * The thread feed: the chat transport's shared core (SUP-256).
 *
 * One Session (single writer) projects to many read-only clients. Message
 * ids are ULIDs, so identity IS order: the transcript is the id-sorted set
 * and no frame carries a position. A contiguous version `v` stamps settled
 * state; the live streaming message rides unversioned frames.
 *
 * Three rules: a `put` applies iff `v === have + 1`, anything else resyncs
 * by reconnecting with `?v=have` (the join path); snapshots stage until
 * `done` then swap wholesale, so removals need no protocol; stream frames
 * apply only over state that already holds the live message.
 *
 * Imported by BOTH sides — the DO emits, the client reduces — so tests pin
 * the round-trip against the shipping rule, never a copy.
 */
import type { UIMessage } from "ai";
import { monotonicFactory } from "ulid";

export type ThreadPhase = "idle" | "streaming" | "recovering";

/** Fields that grow by append while a message streams. `inputText` is raw
 *  accumulated tool-input JSON; the client parses it for display. */
export type StreamField = "text" | "inputText";

export type StreamingPart = UIMessage["parts"][number] & {
  text?: string;
  inputText?: string;
};

export type FeedFrame =
  | { t: "hello"; v: number; phase: ThreadPhase }
  | {
      t: "snapshot";
      v: number;
      phase: ThreadPhase;
      part: UIMessage[];
      done: boolean;
    }
  /** Settled change: the touched messages. `[]` is a pure phase change. */
  | { t: "put"; v: number; phase: ThreadPhase; messages: UIMessage[] }
  | { t: "stream-set"; id: string; message: UIMessage }
  | {
      t: "stream-append";
      id: string;
      part: number;
      field: StreamField;
      delta: string;
    };

// ── ULID ids (identity is order) ─────────────────────────────────────────

const mint = monotonicFactory();

/** Monotonic within this realm, so same-ms mints keep their mint order. */
export function mintUlid(now = Date.now()): string {
  return mint(now);
}

/** An id strictly greater than `prev`, for ANY string `prev` — a fresh
 *  ULID when it clears, else `prev` plus the fresh mint's tail (unique and
 *  mint-ordered even when concurrent mints race the same max). */
export function idAfter(prev: string, now = Date.now()): string {
  const fresh = mintUlid(now);
  return fresh > prev ? fresh : prev + "0" + fresh.slice(-8);
}

// ── Client reducer ───────────────────────────────────────────────────────

export interface FeedState {
  /** Settled version; null until first sync (surfaces render a skeleton). */
  v: number | null;
  phase: ThreadPhase;
  messages: ReadonlyMap<string, UIMessage>;
  /** Snapshot batches staged until `done` swaps them in. */
  staging: UIMessage[] | null;
}

export function emptyFeedState(): FeedState {
  return { v: null, phase: "idle", messages: new Map(), staging: null };
}

/** Seed from a locally cached snapshot (paint before connect). */
export function cachedFeedState(
  v: number,
  phase: ThreadPhase,
  messages: UIMessage[],
): FeedState {
  return {
    v,
    phase,
    messages: new Map(messages.map((m) => [m.id, m])),
    staging: null,
  };
}

export interface Reduced {
  state: FeedState;
  /** Frame couldn't apply — reconnect with `?v=state.v`. */
  resync: boolean;
}

const ok = (state: FeedState): Reduced => ({ state, resync: false });

export function reduceFeed(state: FeedState, frame: FeedFrame): Reduced {
  switch (frame.t) {
    case "hello":
      // A version match confirms the cache; otherwise await the snapshot.
      return state.v === frame.v
        ? ok({ ...state, phase: frame.phase })
        : ok({ ...state, staging: null });
    case "snapshot": {
      const staged = [...(state.staging ?? []), ...frame.part];
      if (!frame.done) return ok({ ...state, staging: staged });
      return ok({
        v: frame.v,
        phase: frame.phase,
        messages: new Map(staged.map((m) => [m.id, m])),
        staging: null,
      });
    }
    case "put": {
      // Pre-sync puts drop: the snapshot built after them carries all.
      if (state.v === null) return ok(state);
      if (frame.v !== state.v + 1) return { state, resync: true };
      const messages = new Map(state.messages);
      for (const m of frame.messages) messages.set(m.id, m);
      return ok({ v: frame.v, phase: frame.phase, messages, staging: null });
    }
    case "stream-set": {
      if (state.v === null) return ok(state);
      const messages = new Map(state.messages);
      messages.set(frame.id, frame.message);
      return ok({ ...state, messages });
    }
    case "stream-append": {
      if (state.v === null) return ok(state);
      const existing = state.messages.get(frame.id);
      const part = existing?.parts[frame.part] as StreamingPart | undefined;
      if (!existing || part === undefined) return { state, resync: true };
      const parts = existing.parts.slice();
      parts[frame.part] = {
        ...part,
        [frame.field]: (part[frame.field] ?? "") + frame.delta,
      } as StreamingPart;
      const messages = new Map(state.messages);
      messages.set(frame.id, { ...existing, parts });
      return ok({ ...state, messages });
    }
  }
}

/** The rendered view: id-sorted, null until synced. */
export function feedView(
  state: FeedState,
): { messages: UIMessage[]; phase: ThreadPhase } | null {
  if (state.v === null) return null;
  const messages = [...state.messages.values()].sort((a, b) =>
    a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
  );
  return { messages, phase: state.phase };
}

// ── Server stream diff ───────────────────────────────────────────────────

/** Last flushed live-message shape: enough to prove "only text grew".
 *  Anything else falls back to a wholesale `stream-set`. */
export interface StreamTracker {
  id: string;
  parts: { sig: string; text: number; inputText: number }[];
}

function partSig(part: StreamingPart): string {
  const p = part as Record<string, unknown>;
  return `${part.type}|${String(p.state ?? "")}|${String(p.toolCallId ?? "")}`;
}

function trackerFor(id: string, message: UIMessage): StreamTracker {
  return {
    id,
    parts: (message.parts as StreamingPart[]).map((p) => ({
      sig: partSig(p),
      text: typeof p.text === "string" ? p.text.length : -1,
      inputText: typeof p.inputText === "string" ? p.inputText.length : -1,
    })),
  };
}

/** Diff the live message against the tracker: appends on the hot path, one
 *  `stream-set` on any doubt. O(parts), never the thread. */
export function streamDelta(
  tracker: StreamTracker | null,
  message: UIMessage,
): { frames: FeedFrame[]; tracker: StreamTracker } {
  const next = trackerFor(message.id, message);
  const set: { frames: FeedFrame[]; tracker: StreamTracker } = {
    frames: [{ t: "stream-set", id: message.id, message }],
    tracker: next,
  };
  if (
    tracker === null ||
    tracker.id !== message.id ||
    tracker.parts.length > next.parts.length
  ) {
    return set;
  }
  const frames: FeedFrame[] = [];
  const parts = message.parts as StreamingPart[];
  for (let i = 0; i < next.parts.length; i++) {
    const prev = tracker.parts[i];
    const cur = next.parts[i]!;
    if (prev === undefined) return set;
    if (prev.sig !== cur.sig) return set;
    for (const field of ["text", "inputText"] as const) {
      if (prev[field] === cur[field]) continue;
      if (prev[field] < 0 || cur[field] < prev[field]) return set;
      frames.push({
        t: "stream-append",
        id: message.id,
        part: i,
        field,
        delta: (parts[i]![field] as string).slice(prev[field]),
      });
    }
  }
  return { frames, tracker: next };
}

// ── Settled digest ───────────────────────────────────────────────────────

/** FNV-1a over the settled view, stored beside the version — how a wake
 *  detects a change whose broadcast died with the last activation. A
 *  collision costs one missed broadcast, healed by the next resync. */
export function settledDigest(
  phase: ThreadPhase,
  entries: Iterable<readonly [string, string]>,
): string {
  let h = 0x811c9dc5;
  const mix = (s: string) => {
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    h ^= 0x1f;
    h = Math.imul(h, 0x01000193) >>> 0;
  };
  mix(phase);
  for (const [id, json] of entries) {
    mix(id);
    mix(json);
  }
  return h.toString(16);
}

// ── Frame serialization ──────────────────────────────────────────────────

export const SNAPSHOT_BATCH_BYTES = 256 * 1024;

/** Per-frame wire budget in BYTES, under Cloudflare's 1 MiB WS cap. Rows
 *  can legitimately reach 1.8 MB, so frames must shrink, not trust. */
export const FRAME_WIRE_BYTES = 900 * 1024;

function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

const PAYLOAD_FIELDS = [
  "output",
  "text",
  "inputText",
  "input",
  "data",
] as const;

/** The smallest string worth eliding: below this the marker costs as much. */
const MIN_ELIDABLE_BYTES = 1024;

const elision = (bytes: number) =>
  `[${bytes} bytes too large for chat transport]`;

/** Rewrite every string in a part's payload fields. */
function mapPayload(
  part: unknown,
  rewrite: (value: string) => string,
): unknown {
  const map = (value: unknown): unknown => {
    if (typeof value === "string") return rewrite(value);
    if (Array.isArray(value)) return value.map(map);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value).map(([k, v]) => [k, map(v)]),
      );
    }
    return value;
  };
  let next = part as Record<string, unknown>;
  for (const f of PAYLOAD_FIELDS) {
    if (f in next) next = { ...next, [f]: map(next[f]) };
  }
  return next;
}

/**
 * Elide every payload string at or above the size that makes the message fit,
 * wherever it sits, rather than blanking the whole field holding it. A tool
 * result is a shape, not a blob: a PDF read carries a reading-sized page
 * render AND a small preview of it, and blanking the field took both.
 */
function capForWire(message: UIMessage, budget: number): UIMessage {
  const sizes: number[] = [];
  for (const part of message.parts) {
    mapPayload(part, (value) => {
      sizes.push(byteLength(value));
      return value;
    });
  }
  sizes.sort((a, b) => b - a);

  let remaining = byteLength(JSON.stringify(message));
  let cutoff = Infinity;
  for (const bytes of sizes) {
    if (remaining <= budget || bytes < MIN_ELIDABLE_BYTES) break;
    remaining -= bytes - elision(bytes).length;
    cutoff = bytes;
  }
  if (cutoff === Infinity) return message;

  const parts = message.parts.map((part) =>
    mapPayload(part, (value) => {
      const bytes = byteLength(value);
      return bytes >= cutoff ? elision(bytes) : value;
    }),
  );
  return { ...message, parts } as unknown as UIMessage;
}

/** Terminal fallback: a stub fits whatever shape carried the weight. */
function stubForWire(message: UIMessage): UIMessage {
  return {
    id: message.id,
    role: message.role,
    parts: [{ type: "text", text: "[message too large for chat transport]" }],
    metadata: { truncatedForWire: true },
  } as UIMessage;
}

/** THE frame serializer — the one door onto the socket, so no frame can
 *  exceed the wire cap. Small frames pay one stringify + length check. */
export function encodeFrame(frame: FeedFrame): string {
  const json = JSON.stringify(frame);
  // UTF-8 is ≤ 3 bytes per UTF-16 code unit; skip the encode when safe.
  if (json.length <= FRAME_WIRE_BYTES / 3) return json;
  if (byteLength(json) <= FRAME_WIRE_BYTES) return json;
  const messages =
    frame.t === "snapshot"
      ? frame.part
      : frame.t === "put"
        ? frame.messages
        : frame.t === "stream-set"
          ? [frame.message]
          : null;
  if (messages === null) return json;
  const budget = Math.floor(FRAME_WIRE_BYTES / Math.max(1, messages.length));
  const capped = messages.map((m) => {
    const kept = capForWire(m, budget);
    return byteLength(JSON.stringify(kept)) <= budget ? kept : stubForWire(m);
  });
  return JSON.stringify(
    frame.t === "snapshot"
      ? { ...frame, part: capped }
      : frame.t === "put"
        ? { ...frame, messages: capped }
        : { ...frame, message: capped[0]! },
  );
}

export function batchSnapshot(
  v: number,
  phase: ThreadPhase,
  messages: UIMessage[],
  maxBytes = SNAPSHOT_BATCH_BYTES,
): FeedFrame[] {
  const frames: FeedFrame[] = [];
  let part: UIMessage[] = [];
  let bytes = 0;
  for (const m of messages) {
    const size = JSON.stringify(m).length;
    if (part.length > 0 && bytes + size > maxBytes) {
      frames.push({ t: "snapshot", v, phase, part, done: false });
      part = [];
      bytes = 0;
    }
    part.push(m);
    bytes += size;
  }
  frames.push({ t: "snapshot", v, phase, part, done: true });
  return frames;
}
